/**
 * Which assignment statuses mean a client is ON the plan today — asked in two places, decided here.
 *
 * `PlanAssignmentStatus` has four members: `Active`, `Paused`, `Completed`, `Cancelled`. Two of
 * them count, and the population they define is the one the library's own `assignedClientCount` is
 * built from — so the inspector's follower strip and the `Plany klientów` list must agree with it
 * and with each other. They did not share a rule: the strip carried a `LIVE_STATUSES` array and the
 * list a `STATUSES` map, and a fifth status arriving on the wire would have had to be remembered
 * twice.
 *
 * **A paused client still HAS the plan.** The trainer resumes it rather than reassigning, so
 * pausing must not empty the strip or drop the row. A completed or cancelled one does: a plan
 * somebody finished is history, and history belongs on that client's own screen rather than in the
 * trainer's list of what is running. `Cancelled` is filtered server-side and is named here anyway,
 * because a status this app refuses should not depend on a filter it cannot see.
 *
 * **The allow-list is POSITIVE.** A fifth status this build has never heard of is excluded rather
 * than inheriting whichever branch happened to be the `else`.
 *
 * ── It lives in the ENTITY since 17 September 2026, and that is the promotion rule, not a tidy-up ──
 * It was `pages/plan-library/model/assignmentStatus.ts` while the library's two lists were its only
 * readers. The builder's plan menu is the second consumer — it has to know whether the plan it is
 * looking at is a fork somebody is still on — and a page may not reach into another page's model, so
 * the choice was a copy or a promotion. `entities/plan-draft` is where it went because that is
 * already where `GET /plan-assignment` is read from (`api/planForkCustomer`), so the rule about a
 * status now sits beside the request that returns it.
 *
 * No React, no DOM, no query hook, no i18next.
 */

/**
 * The two statuses a row may wear, in this app's own vocabulary rather than the wire's PascalCase.
 * The projection is the seam.
 */
export type ClientPlanStatus = 'active' | 'paused';

/** The wire's spelling of each, matched exactly. */
const LIVE: Readonly<Record<string, ClientPlanStatus>> = {
  Active: 'active',
  Paused: 'paused',
};

/**
 * The status a live assignment wears, or `null` where the assignment is not one this app lists.
 *
 * The single entry point: a caller that only needs the boolean reads `!== null`, and one that needs
 * the named status reads the value, so the two questions cannot drift apart.
 */
export function liveAssignmentStatus(
  status: string | null | undefined,
): ClientPlanStatus | null {
  return LIVE[status?.trim() ?? ''] ?? null;
}
